import { FrameContexts, HostClientType, HostName, UrlDialogInfo } from '@microsoft/teams-js';
import {
  CareerHubSDKContext,
  DialogSubmitHandler,
  ICareerHubAppconfig,
  PostMessageChannel,
  UserRequest,
} from '../../public/careerHub.types';
import { IDialogService } from '../../public/interfaces/IDialogService';
import { AppHostEventListener } from '../services/AppHostEventListener';
import { DialogsMessageHandler } from '../services/DialogsMessageHandler';
import { authentication } from './authentication';
let careerHubContext: CareerHubSDKContext;
let appConfig: ICareerHubAppconfig;
let taskModuleDialogService: IDialogService;
let dialogProcessor: DialogsMessageHandler;
let appHostListener: AppHostEventListener;
let onAutenticationCallback: () => void;

/**
 * Registers app config for standalone hub
 * @param config: ICareerHubAppConfig.
 */
export function registerAppConfig(config: ICareerHubAppconfig, dialogService: IDialogService, callback: () => void) {
  appConfig = config;
  taskModuleDialogService = dialogService;
  dialogProcessor = new DialogsMessageHandler(taskModuleDialogService);
  onAutenticationCallback = callback;
}

export function getFrameContext() {
  return window.parent != window.self ? FrameContexts.task : FrameContexts.content;
}

export namespace app {
  /**
   * Initializes the standalone library. This must be called before any other SDK calls
   * @param validMessageOrigins Optionally specify a list of cross frame message origins. They must have
   * https: protocol otherwise they will be ignored. Example: https://www.example.com
   * @returns Promise that will be fulfilled when initialization has completed
   */
  export function initialize(_validMessageOrigins?: string[]): Promise<void> {
    return new Promise<void>((resolve, _reject) => {
      authentication.initialize(appConfig, () => {
        let frmContext = getFrameContext();
        if (frmContext !== FrameContexts.task) {
          dialogProcessor.initHandlers();
          appHostListener = new AppHostEventListener(window);
          appHostListener.registerEventHandler(dialogProcessor);
          onAutenticationCallback?.();
        }
        resolve();
      });
    });
  }

  export function openLink(link: string): Promise<void> {
    return new Promise<void>((resolve, _reject) => {
      // Navigate to tab based on link
      window.open(link);
      resolve();
    });
  }
  /**
   * Retrieves the current context the frame is running in.
   * Note: the context may not contain teams related information as it is for standalone.
   * @param callback The callback to invoke when the {@link Context} object is retrieved.
   */
  export function getContext(): Promise<CareerHubSDKContext> {
    let frameContext = getFrameContext();
    //TODO: Need to fill some of the context properties. Right now these values are not being used in standalone web app.
    let defaultContext: CareerHubSDKContext = {
      app: {
        locale: 'en-US',
        theme: '',
        sessionId: '',
        host: {
          name: HostName.orange,
          clientType: HostClientType.web,
          sessionId: '',
        },
      },
      page: {
        isFullScreen: true,
        id: '',
        frameContext: frameContext,
      },
      user: undefined,
    };

    return new Promise<CareerHubSDKContext>((resolve, _reject) => {
      const userRequest: UserRequest = {
        successCallback: (profile) => {
          careerHubContext = { ...defaultContext };
          careerHubContext.user = {
            id: profile.oid,
            userPrincipalName: profile.upn,
            loginHint: profile.upn,
          };
          resolve(careerHubContext);
        },
        failureCallback: (reason) => {
          console.log(reason);
          careerHubContext = { ...defaultContext };
          resolve(careerHubContext);
        },
      };
      authentication.getUser(userRequest);
    });
  }

  /**
   * Registers a handler for theme changes.
   * Only one handler can be registered at a time. A subsequent registration replaces an existing registration.
   * @param handler The handler to invoke when the user changes their theme.
   */
  export function registerOnThemeChangeHandler(_handler: (theme: string) => void): void {}
}

/**
 * Namespace to interact with the task module-specific part of the SDK.
 * This object is usable only on the content frame.
 */
export namespace dialog {
  export namespace url {
    /**
     * Allows app to open a url based dialog.
     *
     * @remarks
     * This function cannot be called from inside of a dialog
     *
     * @param urlDialogInfo - An object containing the parameters of the dialog module.
     * @param submitHandler - Handler that triggers when a dialog calls the {@linkcode submit} function or when the user closes the dialog.
     * @param _messageFromChildHandler - Handler that triggers if dialog sends a message to the app.
     */
    export function open(
      urlDialogInfo: UrlDialogInfo,
      submitHandler?: DialogSubmitHandler,
      _messageFromChildHandler?: PostMessageChannel
    ): void {
      if (getFrameContext() === FrameContexts.task) {
        console.error('startTask does not work in Task module');
        return;
      }

      taskModuleDialogService.open(urlDialogInfo, submitHandler);
    }

    /**
     * Submit the task module.
     * @param result Contains the result to be sent to the bot or the app. Typically a JSON object or a serialized version of it
     * @param _appIds Helps to validate that the call originates from the same appId as the one that invoked the task module
     */
    export function submit(result?: string | object, _appIds?: string | string[]): void {
      if (getFrameContext() === FrameContexts.content) {
        console.error('submitTask does not work in the main host');
        return;
      }

      sendMessageToParent('dialogs.submit', result);
    }
  }
}

// tslint:disable-next-line:no-any
function createMessageRequest(func: string, args: any): any {
  return {
    id: 1,
    func: func,
    timestamp: Date.now(),
    args: args || [],
  };
}

export function sendMessageToParent(actionName: string, argsOrCallback?: any): void {
  let args: any | undefined;
  args = argsOrCallback;

  const targetWindow = window.parent;
  if (targetWindow !== window.self) {
    const request = createMessageRequest(actionName, args);
    targetWindow.postMessage(request, window.origin);
  }
}
